import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, Edit2, Users, Building2, GitBranch, Save, X, Trash2, Lock, Unlock, Sparkles, ToggleLeft, ToggleRight, Info, Mail, Bell, Send } from 'lucide-react';
import { invalidateAIFeaturesCache } from '../hooks/useAIFeatures';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import Badge from '../components/Badge';
import { FF, ErrMsg } from '../components/FormField';
import { PageHeader } from '../components/FilterBar';

const ROLES = ['admin','it_staff','junior_it','director','finance_staff','general_user'];
const ROLE_LABELS = { admin:'Admin', it_staff:'IT Staff', junior_it:'Junior IT', director:'Director', finance_staff:'Finance Staff', general_user:'General User' };

export default function Settings() {
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]); const [companies, setCompanies] = useState([]); const [departments, setDepartments] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null); const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({}); const [saving, setSaving] = useState(false); const [error, setError] = useState('');
  const [userCompanyAccess, setUserCompanyAccess] = useState([]); // multi-company selection
  const [coSearch, setCoSearch] = useState(''); const [deptSearch, setDeptSearch] = useState(''); const [deptFilterCo, setDeptFilterCo] = useState('');
  const [aiFeatures, setAiFeatures] = useState({ chat:true, forecast:true, suggestions:true, nl_query:true, anomalies:true });
  const [aiSaving, setAiSaving] = useState(false); const [aiSaved, setAiSaved] = useState(false);
  const [emailToggles, setEmailToggles] = useState({});
  const [emailSaving, setEmailSaving] = useState(false); const [emailSaved, setEmailSaved] = useState(false);
  // Email notices
  const [noticeMode, setNoticeMode] = useState('company'); // 'company' | 'users'
  const [noticeCompany, setNoticeCompany] = useState(''); const [noticeDept, setNoticeDept] = useState('');
  const [noticeUsers, setNoticeUsers] = useState([]); // selected employee IDs for 'users' mode
  const [allNoticeUsers, setAllNoticeUsers] = useState([]); const [noticeUserSearch, setNoticeUserSearch] = useState('');
  const [noticeSubject, setNoticeSubject] = useState(''); const [noticeMsg, setNoticeMsg] = useState('');
  const [noticePreview, setNoticePreview] = useState([]); const [noticePreviewLoading, setNoticePreviewLoading] = useState(false);
  const [noticeSending, setNoticeSending] = useState(false); const [noticeSent, setNoticeSent] = useState(null);
  const [noticeError, setNoticeError] = useState(''); const [noticeDepts, setNoticeDepts] = useState([]);
  const [noticeFile, setNoticeFile] = useState(null);

  useEffect(() => {
    api.get('/settings/ai-features').then(r => setAiFeatures(r.data)).catch(() => {});
    api.get('/settings/email-toggles').then(r => setEmailToggles(r.data)).catch(() => {});
  }, []);

  const saveAiFeatures = async () => {
    setAiSaving(true); setAiSaved(false);
    try {
      await api.put('/settings/ai-features', aiFeatures);
      invalidateAIFeaturesCache();
      setAiSaved(true); setTimeout(() => setAiSaved(false), 2000);
    } catch {} finally { setAiSaving(false); }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [u, c, d] = await Promise.all([
        api.get('/settings/users').then(r => r.data).catch(() => []),
        api.get('/master/companies').then(r => r.data).catch(() => []),
        api.get('/master/departments').then(r => r.data).catch(() => []),
      ]);
      setUsers(u); setCompanies(c); setDepartments(d);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleSaveUser = async e => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      let userId;
      if (selected) {
        await api.put(`/settings/users/${selected.id}`, form);
        userId = selected.id;
      } else {
        const res = await api.post('/settings/users', form);
        userId = res.data.id;
      }
      await api.post(`/master-files/user-access/${userId}`, { company_ids: userCompanyAccess });
      setModal(null); setSelected(null); setUserCompanyAccess([]); fetchAll();
    } catch (err) { setError(err.response?.data?.error || 'Save failed'); } finally { setSaving(false); }
  };

  const handleDeleteUser = async (u) => {
    if (!window.confirm(`Permanently delete user "${u.username}"?\n\nThis will:\n• Remove their login permanently\n• Free their username and email for reuse\n• Cannot be undone`)) return;
    try { await api.delete(`/settings/users/${u.id}`); fetchAll(); }
    catch (err) { alert(err.response?.data?.error || 'Delete failed'); }
  };

  const handleToggleActive = async (u) => {
    const action = u.is_active ? 'deactivate' : 'reactivate';
    if (!window.confirm(`${u.is_active ? 'Deactivate' : 'Reactivate'} user "${u.username}"?`)) return;
    try { await api.put(`/settings/users/${u.id}`, { is_active: !u.is_active }); fetchAll(); }
    catch (err) { alert('Update failed'); }
  };

  const handleSaveCompany = async e => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      selected ? await api.put(`/master/companies/${selected.id}`, form) : await api.post('/master/companies', form);
      setModal(null); setSelected(null); fetchAll();
    } catch (err) { setError(err.response?.data?.error || 'Save failed'); } finally { setSaving(false); }
  };

  const handleSaveDept = async e => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      selected ? await api.put(`/master/departments/${selected.id}`, form) : await api.post('/master/departments', form);
      setModal(null); setSelected(null); fetchAll();
    } catch (err) { setError(err.response?.data?.error || 'Save failed'); } finally { setSaving(false); }
  };

  const saveEmailToggles = async () => {
    setEmailSaving(true); setEmailSaved(false);
    try { await api.put('/settings/email-toggles', emailToggles); setEmailSaved(true); setTimeout(() => setEmailSaved(false), 2500); }
    catch { alert('Save failed'); } finally { setEmailSaving(false); }
  };

  const EMAIL_GROUPS = [
    { label: 'Authentication', color: '#f87171', items: [
      { key: 'otp_login',          label: 'OTP — Login',          desc: '⚠ Turning this OFF will immediately prevent all users from logging in — use as emergency system lockout if needed' },
      { key: 'otp_password_reset', label: 'OTP — Password Reset', desc: 'One-time password sent during forgot-password flow' },
    ]},
    { label: 'Employees', color: '#34d399', items: [
      { key: 'employee_welcome',      label: 'Welcome / Account Created', desc: 'Sent when a new employee SynergyNode account is created' },
      { key: 'system_login_created',  label: 'System Login Created',      desc: 'Sent when a new system login is added under an employee' },
      { key: 'email_account_created', label: 'Email Account Created',     desc: 'Sent when a new email account is added under an employee' },
    ]},
    { label: 'Assets & Accessories', color: '#fb923c', items: [
      { key: 'asset_allocated',      label: 'Asset Allocated',         desc: 'Sent to the employee when an asset is allocated to them' },
      { key: 'accessory_allocated',  label: 'Accessory Allocated',     desc: 'Sent to the employee when an accessory is assigned to them' },
      { key: 'accessory_request',    label: 'Accessory Request',       desc: 'Sent on new requests and status updates (approved / rejected / completed)' },
      { key: 'dongle_allocated',     label: 'Dongle Allocated',        desc: 'Sent when a data dongle is assigned to an employee' },
      { key: 'voice_allocated',      label: 'Voice Number Allocated',  desc: 'Sent when a voice number is assigned to an employee' },
    ]},
    { label: 'Repairs', color: '#fbbf24', items: [
      { key: 'repair_logged', label: 'Repair Logged', desc: 'Sent to the asset holder when a repair record is created' },
    ]},
    { label: 'IT Tickets', color: '#a78bfa', items: [
      { key: 'ticket_raised',  label: 'Ticket Raised',        desc: 'Sent to the employee + HOD when a new support ticket is submitted' },
      { key: 'ticket_updated', label: 'Ticket In Progress',   desc: 'Sent when IT marks a ticket as In Progress' },
      { key: 'ticket_resolved',label: 'Ticket Resolved',      desc: 'Sent with satisfaction survey link when a ticket is resolved' },
      { key: 'ticket_closed',  label: 'Ticket Closed',        desc: 'Sent when IT closes a resolved ticket' },
      { key: 'ticket_overdue', label: 'Overdue Ticket Reminder', desc: 'Sent to IT Head when tickets exceed 48 hours without resolution' },
      { key: 'ticket_board_approval', label: 'Board Approval Request', desc: 'Sent to the approver the admin chooses for that request, with one-time Approve/Reject links. Also covers the reminder and the withdrawal notice when a request is cancelled.' },
    ]},
    { label: 'Offboarding', color: '#f472b6', items: [
      { key: 'offboarding_initiated', label: 'Offboarding Initiated — Handover Notice', desc: 'Sent to the employee when offboarding is initiated, listing all IT assets and accessories they must hand back to IT on their last day. CC\'d to IT dept.' },
      { key: 'offboarding_reminder',  label: 'Offboarding Handover Reminder', desc: 'The same handover notice re-sent as a kind reminder, to the same recipients (employee, CC IT dept + HOD): automatically once, 3 days before the last working day, and whenever an admin presses Send Reminder on the offboarding. Switching this off stops both.' },
      { key: 'offboarding_cancelled', label: 'Offboarding Cancelled — Employee Continues', desc: 'Sent when an admin cancels an offboarding because the employee decided to stay: to the employee, CC IT dept, their HOD and HR (synergyhr@clsynergy.com), with the reason and any access or items IT must restore.' },
    ]},
    { label: 'Automation Alerts', color: '#22d3ee', items: [
      { key: 'ticket_escalated',       label: 'Ticket Auto-Escalation',        desc: 'Sent to IT Head when tickets open >4 days are auto-escalated to High priority (checked hourly)' },
      { key: 'ticket_autoclose',       label: 'Ticket Auto-Close Notice',      desc: 'Sent to the employee when a resolved ticket is closed automatically after 5 days' },
      { key: 'eol_autoflag',           label: 'EOL Auto-Flag Digest',          desc: 'Sent to IT Head when assets past their EOL / depreciation end date are auto-flagged (daily 7:30 AM)' },
      { key: 'toner_low_stock',        label: 'Toner Low Stock Alert',         desc: 'Sent to IT Head when toner stock falls to/below the reorder level (once per dip)' },
      { key: 'warranty_expiry_alert',  label: 'Warranty & License Expiry Digest', desc: 'Weekly Monday digest of asset/accessory warranties and licenses expiring within 60 days' },
      { key: 'policy_ack_reminder',    label: 'Policy Acknowledgement Chaser', desc: 'Weekly Monday reminder to users with pending policy acknowledgements (fresh one-time link)' },
      { key: 'low_satisfaction_alert', label: 'Low Satisfaction Alert',        desc: 'Sent to IT Head immediately when a satisfaction survey is rated 4 or below (out of 10)' },
      { key: 'renewal_alert',          label: 'IT Renewal Due Alert',          desc: 'Sent to IT Head (CC IT dept) 30, 15 and 7 days before a renewal falls due, then every 7 days while it stays overdue (daily 8:00 AM)' },
    ]},
  ];

  const tabs = [
    ['users', 'System Users', <Users size={13}/>],
    ['companies', 'Companies', <Building2 size={13}/>],
    ['departments', 'Departments', <GitBranch size={13}/>],
    ...(isAdmin ? [['emails', 'Emails', <Mail size={13}/>]] : []),
    ...(isAdmin ? [['ai', 'AI Features', <Sparkles size={13}/>]] : []),
    ...(isAdmin ? [['notices', 'Email Notices', <Bell size={13}/>]] : [])
  ];

  const AI_FEATURE_DEFS = [
    {
      key: 'chat',
      icon: '💬',
      label: 'AI Helpdesk Chat',
      tagline: 'Self-service IT support for all employees, 24/7',
      desc: 'A conversational AI assistant that answers IT questions instantly. It knows the employee\'s assigned assets and recent tickets, and can suggest raising a pre-filled support ticket if the issue needs human attention.',
      where: 'General User Portal (My Profile page)',
      nav: 'Sidebar → My Portal → look for the purple bot button at the bottom-right corner of the page',
      roles: ['All employees (general_user, all roles)'],
      steps: [
        'Employee opens the General User Portal',
        'A purple 🤖 bot button appears at the bottom-right of the screen',
        'Click it to open the chat panel',
        'Type any IT question — e.g. "my laptop won\'t connect to WiFi" or "how do I reset my password"',
        'AI responds instantly with troubleshooting steps',
        'If the issue needs IT staff, AI shows a "Raise Ticket" button with a pre-filled title',
      ],
    },
    {
      key: 'forecast',
      icon: '📈',
      label: 'Asset Replacement Forecast',
      tagline: 'Predictive budget planning based on asset age and repair history',
      desc: 'AI analyses all laptops, desktops, servers, printers and monitors — considering age (4+ years = EOL), number of repairs, and repair cost vs purchase price — to forecast which assets need replacement and estimate the budget required.',
      where: 'AI Insights page — Asset Forecast tab',
      nav: 'Sidebar → AI Insights → click the "Asset Forecast" tab → click "Generate Forecast"',
      roles: ['Admin', 'IT Staff', 'Director'],
      steps: [
        'Navigate to AI Insights in the sidebar',
        'Click the "Asset Forecast" tab',
        'Click "Generate Forecast" — AI analyses up to 80 assets with their repair history',
        'View the executive summary, risk level, and budget estimate',
        'See critical assets that need immediate replacement',
        'See planned replacements for the next 12 months',
        'Review budget breakdown by category (laptop, desktop, server, etc.) and company',
      ],
    },
    {
      key: 'suggestions',
      icon: '💡',
      label: 'Ticket Resolution Suggestions',
      tagline: 'AI-powered resolution guidance for IT staff based on past tickets',
      desc: 'When resolving a ticket, IT staff can request AI suggestions. The AI searches similar resolved tickets and provides specific step-by-step troubleshooting guidance. The suggested steps can be used directly as resolution notes with one click.',
      where: 'IT Tickets → Resolve modal',
      nav: 'Sidebar → IT Tickets → click "Resolve" on any open ticket → see the purple "Get AI Suggestions" button',
      roles: ['IT Staff', 'Admin'],
      steps: [
        'Open the IT Tickets page',
        'Click "Resolve" on an open or in-progress ticket',
        'In the resolve form, click the purple "Get AI Suggestions" button',
        'AI searches similar resolved tickets and generates specific resolution steps',
        'Review the suggested "Likely Cause" and "Resolution Steps"',
        'Click "↓ Use this as resolution notes" to copy the suggestion into the notes field',
        'Edit if needed, then complete the resolution as normal',
      ],
    },
    {
      key: 'nl_query',
      icon: '🔍',
      label: 'Natural Language Query',
      tagline: 'Ask questions about your data in plain English — no SQL needed',
      desc: 'A text input on the Analysis page where users can type plain-English questions about IT data. AI converts the question to a PostgreSQL query, validates it is read-only, executes it, and displays the results as a table alongside the generated SQL.',
      where: 'Analysis page — AI Query Assistant panel (bottom of page)',
      nav: 'Sidebar → Analysis → scroll to the bottom of the page to the purple "AI Query Assistant" section',
      roles: ['Admin', 'IT Staff', 'Director'],
      steps: [
        'Navigate to the Analysis page',
        'Scroll to the "AI Query Assistant" panel at the bottom',
        'Type a question in plain English — e.g. "How many laptops are in use at CLS?"',
        'Or click one of the quick-example buttons to start with a preset question',
        'Press Enter or click "Ask AI"',
        'AI generates the SQL query, executes it, and shows the results as a table',
        'The generated SQL is displayed for transparency',
      ],
    },
    {
      key: 'anomalies',
      icon: '🔍',
      label: 'Anomaly Detection',
      tagline: 'Proactive alerts for operational risks before they become problems',
      desc: 'AI scans 7 operational data points: assets repaired multiple times, employees with repeated damage incidents, licenses expiring within 60 days, tickets open over 3 days, toner below reorder level, laptops/desktops over 4 years old still in use, and ticket volume trends. Returns severity-ranked anomalies with specific recommended actions.',
      where: 'AI Insights page — Anomaly Detection tab',
      nav: 'Sidebar → AI Insights → Anomaly Detection tab is the default → click "Run Detection"',
      roles: ['Admin', 'IT Staff', 'Director'],
      steps: [
        'Navigate to AI Insights in the sidebar',
        'The Anomaly Detection tab is selected by default',
        'Click "Run Detection" — AI scans all 7 data patterns simultaneously',
        'View the System Health indicator (Good / Fair / Warning / Critical)',
        'Read the executive summary',
        'Click on any anomaly card to expand it and see the full description and recommendation',
        'High-priority anomalies appear first in red, then medium in orange, then low in yellow',
      ],
    },
  ];

  const TH = ({ cols }) => <thead><tr style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
    {cols.map(c => <th key={c} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#475569', whiteSpace: 'nowrap' }}>{c}</th>)}
  </tr></thead>;
  const TD = ({ children }) => <td style={{ padding: '10px 16px', color: '#94a3b8', fontSize: 13, borderBottom: '1px solid rgba(255,255,255,0.04)' }}>{children}</td>;

  return (
    <div className="space-y-4">
      <PageHeader title="Settings">
        <button onClick={fetchAll} className="btn-ghost"><RefreshCw size={14} className={loading ? 'animate-spin' : ''}/></button>
      </PageHeader>

      <div className="flex gap-1 p-1 rounded-xl w-fit" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
        {tabs.map(([key, label, icon]) => (
          <button key={key} onClick={() => setTab(key)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all"
            style={{ background: tab===key?'rgba(59,130,246,0.2)':'transparent', color: tab===key?'#60a5fa':'#475569', border: tab===key?'1px solid rgba(59,130,246,0.3)':'1px solid transparent' }}>
            {icon}{label}
          </button>
        ))}
      </div>

      {/* ── DATA SCOPE NOTICE — admin only ── */}
      {isAdmin && (
        <div className="rounded-2xl px-5 py-4" style={{ background:'rgba(14,165,233,0.06)', border:'1px solid rgba(14,165,233,0.2)' }}>
          <div className="flex items-start gap-3">
            <Info size={16} style={{ color:'#38bdf8', flexShrink:0, marginTop:1 }}/>
            <div>
              <div className="text-xs font-semibold mb-1.5" style={{ color:'#38bdf8', letterSpacing:'0.04em' }}>DATA ENTRY SCOPE — FY 2026/27 (Effective 1 Apr 2026)</div>
              <div className="text-xs" style={{ color:'#64748b', lineHeight:1.7 }}>
                The following modules have data entered into SynergyNode <strong style={{color:'#94a3b8'}}>from FY 2026/27 onwards</strong>. Records prior to 1 April 2026 are not reflected in this system.
              </div>
              <div className="flex flex-wrap gap-2 mt-2.5">
                {['Repairs','Damages','Accessories','Insurance Claims','Toner'].map(m => (
                  <span key={m} style={{ background:'rgba(14,165,233,0.1)', border:'1px solid rgba(14,165,233,0.22)', color:'#7dd3fc', fontSize:11, fontWeight:500, padding:'2px 10px', borderRadius:20 }}>{m}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── USERS ── */}
      {tab === 'users' && (
        <div className="space-y-3">
          <div className="flex gap-2 items-center flex-wrap">
            {isAdmin && <button onClick={() => { setSelected(null); setForm({ role:'it_staff', is_active:true }); setUserCompanyAccess([]); setModal('user'); }} className="btn-primary"><Plus size={13}/> Add User</button>}
            <input value={userSearch} onChange={e=>setUserSearch(e.target.value)} placeholder="Search by name, username, email, or role…" className="input-dark" style={{maxWidth:300,fontSize:12}}/>
            {userSearch && <button onClick={()=>setUserSearch('')} className="btn-ghost text-xs" style={{color:'#ef4444'}}>Clear</button>}
          </div>
          <div className="glass glow-border rounded-2xl overflow-hidden">
            <table className="w-full" style={{ fontSize: 13 }}>
              <TH cols={['Name','Username','Email','Role','Company','Status','']}/>
              <tbody>{users.filter(u => {
                if (!userSearch) return true;
                const q = userSearch.toLowerCase();
                const rolePretty = (u.role||'').replace(/_/g,' ');
                return (u.full_name||'').toLowerCase().includes(q) ||
                  (u.username||'').toLowerCase().includes(q) ||
                  (u.email||'').toLowerCase().includes(q) ||
                  rolePretty.includes(q) ||
                  (u.Company?.name||'').toLowerCase().includes(q);
              }).map(u => {
                const offboarded = !u.is_active && ['resigned','terminated'].includes(u.employee?.status);
                const pendingActivation = !u.is_active && !!u.employee_id && !offboarded;
                return (
                <tr key={u.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)',transition:'background 0.15s', opacity: u.is_active ? 1 : 0.75, background: pendingActivation ? 'rgba(245,158,11,0.04)' : 'transparent' }} onMouseEnter={e=>e.currentTarget.style.background= pendingActivation ? 'rgba(245,158,11,0.08)' : 'rgba(59,130,246,0.04)'} onMouseLeave={e=>e.currentTarget.style.background= pendingActivation ? 'rgba(245,158,11,0.04)' : 'transparent'}>
                  <TD><span style={{color:'#e2e8f0',fontWeight:500}}>{u.full_name}</span></TD>
                  <TD><span style={{fontFamily:'monospace',color:'#60a5fa'}}>{u.username}</span></TD>
                  <TD>{u.email}</TD>
                  <TD><Badge value={u.role}/></TD>
                  <TD>{u.Company?.name || '—'}</TD>
                  <TD>
                    <Badge value={offboarded ? 'offboarded' : (u.is_active?'active':'inactive')}/>
                    {pendingActivation && <div className="text-xs mt-0.5" style={{color:'#f59e0b'}}>⚠ Pending activation</div>}
                  </TD>
                  <TD>
                    {isAdmin && (
                      <div className="flex gap-1">
                        <button onClick={async () => {
                          setSelected(u); setForm({...u});
                          const acc = await api.get(`/master-files/user-access/${u.id}`).then(r=>r.data).catch(()=>[]);
                          setUserCompanyAccess(acc.map(a=>a.company_id));
                          setModal('user');
                        }} className="btn-ghost px-1.5 py-1 text-xs" title="Edit"><Edit2 size={11}/></button>
                        <button onClick={() => handleToggleActive(u)} className="btn-ghost px-1.5 py-1 text-xs" title={u.is_active ? 'Deactivate' : 'Reactivate'}
                          style={{color: u.is_active ? '#f59e0b' : '#34d399'}}>
                          {u.is_active ? <Lock size={11}/> : <Unlock size={11}/>}
                        </button>
                        <button onClick={() => handleDeleteUser(u)} className="btn-ghost px-1.5 py-1 text-xs" title="Delete permanently" style={{color:'#ef4444'}}>
                          <Trash2 size={11}/>
                        </button>
                      </div>
                    )}
                  </TD>
                </tr>
                );
              })}</tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── COMPANIES ── */}
      {tab === 'companies' && (
        <div className="space-y-3">
          <div className="flex gap-2 items-center">
            {isAdmin && <button onClick={() => { setSelected(null); setForm({ is_active:true }); setModal('company'); }} className="btn-primary"><Plus size={13}/> Add Company</button>}
            <input value={coSearch} onChange={e=>setCoSearch(e.target.value)} placeholder="Search by name or code…" className="input-dark" style={{maxWidth:240,fontSize:12}}/>
          </div>
          <div className="glass glow-border rounded-2xl overflow-hidden">
            <table className="w-full" style={{ fontSize: 13 }}>
              <TH cols={['Code','Name','Address','Status','']}/>
              <tbody>{companies.filter(c => !coSearch || c.name.toLowerCase().includes(coSearch.toLowerCase()) || (c.code||'').toLowerCase().includes(coSearch.toLowerCase())).map(c => (
                <tr key={c.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)',transition:'background 0.15s' }} onMouseEnter={e=>e.currentTarget.style.background='rgba(59,130,246,0.04)'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  <TD><span style={{fontFamily:'monospace',color:'#60a5fa',fontWeight:600}}>{c.code}</span></TD>
                  <TD><span style={{color:'#e2e8f0',fontWeight:500}}>{c.name}</span></TD>
                  <TD>{c.address || '—'}</TD>
                  <TD><Badge value={c.is_active?'active':'inactive'}/></TD>
                  <TD>
                    {isAdmin && <div className="flex gap-1">
                      <button onClick={() => { setSelected(c); setForm({...c}); setModal('company'); }} className="btn-ghost px-2 py-1 text-xs"><Edit2 size={12}/></button>
                      <button onClick={async () => { if (window.confirm(`Delete company "${c.name}"?\n\nThis cannot be undone.`)) { try { await api.delete(`/master/companies/${c.id}`); fetchAll(); } catch(e) { alert(e.response?.data?.error || 'Delete failed'); } }}} className="btn-ghost px-2 py-1 text-xs" style={{color:'#ef4444'}} title="Delete"><Trash2 size={12}/></button>
                    </div>}
                  </TD>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── DEPARTMENTS ── */}
      {tab === 'departments' && (
        <div className="space-y-3">
          <div className="flex gap-2 items-center flex-wrap">
            {isAdmin && <button onClick={() => { setSelected(null); setForm({ is_active:true }); setModal('dept'); }} className="btn-primary"><Plus size={13}/> Add Department</button>}
            <input value={deptSearch} onChange={e=>setDeptSearch(e.target.value)} placeholder="Search by name…" className="input-dark" style={{maxWidth:220,fontSize:12}}/>
            <select value={deptFilterCo} onChange={e=>setDeptFilterCo(e.target.value)} className="input-dark" style={{maxWidth:180,fontSize:12}}>
              <option value="">All Companies</option>
              {companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="glass glow-border rounded-2xl overflow-hidden">
            <table className="w-full" style={{ fontSize: 13 }}>
              <TH cols={['Department Name','Company','Status','']}/>
              <tbody>{departments.filter(d => (!deptSearch || d.name.toLowerCase().includes(deptSearch.toLowerCase())) && (!deptFilterCo || d.company_id === deptFilterCo)).map(d => (
                <tr key={d.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)',transition:'background 0.15s' }} onMouseEnter={e=>e.currentTarget.style.background='rgba(59,130,246,0.04)'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  <TD><span style={{color:'#e2e8f0',fontWeight:500}}>{d.name}</span></TD>
                  <TD>{d.Company?.name || '—'}</TD>
                  <TD><Badge value={d.is_active?'active':'inactive'}/></TD>
                  <TD>
                    {isAdmin && <div className="flex gap-1">
                      <button onClick={() => { setSelected(d); setForm({...d}); setModal('dept'); }} className="btn-ghost px-2 py-1 text-xs"><Edit2 size={12}/></button>
                      <button onClick={async () => { if (window.confirm(`Delete department "${d.name}"?\n\nThis cannot be undone.`)) { try { await api.delete(`/master/departments/${d.id}`); fetchAll(); } catch(e) { alert(e.response?.data?.error || 'Delete failed'); } }}} className="btn-ghost px-2 py-1 text-xs" style={{color:'#ef4444'}} title="Delete"><Trash2 size={12}/></button>
                    </div>}
                  </TD>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── EMAIL TOGGLES TAB ── */}
      {tab === 'emails' && isAdmin && (
        <div className="space-y-5">
          <div className="rounded-2xl p-5" style={{ background:'linear-gradient(135deg,rgba(14,165,233,0.08),rgba(59,130,246,0.04))', border:'1px solid rgba(14,165,233,0.2)' }}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <Mail size={16} style={{ color:'#38bdf8' }}/>
                  <span className="font-bold text-sm" style={{ color:'#38bdf8' }}>Email Notifications — Control Panel</span>
                </div>
                <p className="text-xs" style={{ color:'#64748b', lineHeight:1.6 }}>
                  Turn off email triggers while entering live data to avoid sending bulk notifications to employees. Turn them back on when ready to go live.
                </p>
              </div>
              <button onClick={saveEmailToggles} disabled={emailSaving} className="btn-primary whitespace-nowrap" style={{ fontSize:12 }}>
                {emailSaved ? '✓ Saved' : emailSaving ? 'Saving…' : <><Save size={12} style={{marginRight:4}}/>Save Changes</>}
              </button>
            </div>
          </div>

          {EMAIL_GROUPS.map(group => (
            <div key={group.label} className="glass glow-border rounded-2xl overflow-hidden">
              <div className="px-5 py-3" style={{ background:'rgba(255,255,255,0.03)', borderBottom:'1px solid rgba(255,255,255,0.06)' }}>
                <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: group.color }}>{group.label}</span>
              </div>
              <div className="divide-y" style={{ borderColor:'rgba(255,255,255,0.04)' }}>
                {group.items.map(item => {
                  const on = emailToggles[item.key] !== false;
                  return (
                    <div key={item.key} className="flex items-center justify-between px-5 py-3 gap-4" style={{ transition:'background 0.15s' }}
                      onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.02)'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <div>
                        <div className="text-xs font-medium" style={{ color:'#e2e8f0' }}>{item.label}</div>
                        <div className="text-xs mt-0.5" style={{ color:'#475569' }}>{item.desc}</div>
                      </div>
                      <button onClick={() => setEmailToggles(prev => ({ ...prev, [item.key]: !on }))}
                        style={{ color: on ? '#34d399' : '#ef4444', flexShrink:0 }} title={on ? 'Click to disable' : 'Click to enable'}>
                        {on ? <ToggleRight size={28}/> : <ToggleLeft size={28}/>}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── AI FEATURES TAB ── */}
      {tab === 'ai' && isAdmin && (
        <div className="space-y-5">

          {/* Header banner */}
          <div className="rounded-2xl p-5" style={{ background:'linear-gradient(135deg,rgba(139,92,246,0.12),rgba(99,102,241,0.06))', border:'1px solid rgba(139,92,246,0.25)' }}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <Sparkles size={16} style={{ color:'#a78bfa' }}/>
                  <span className="font-bold text-sm" style={{ color:'#a78bfa' }}>AI Features — Control Panel</span>
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ background:'rgba(139,92,246,0.2)', color:'#c084fc', border:'1px solid rgba(139,92,246,0.3)' }}>Powered by Claude AI</span>
                </div>
                <p className="text-xs leading-relaxed" style={{ color:'#64748b', maxWidth: 600 }}>
                  SynergyNode has <strong style={{ color:'#94a3b8' }}>5 AI features</strong> built in. Toggle each one independently below.
                  Disabled features are hidden from the UI and blocked on the server.
                  All features require <code style={{ color:'#a78bfa', background:'rgba(139,92,246,0.15)', padding:'1px 5px', borderRadius:3 }}>ANTHROPIC_API_KEY</code> to be set in the server <code style={{ color:'#a78bfa' }}>.env</code> file.
                </p>
              </div>
              <button onClick={saveAiFeatures} disabled={aiSaving}
                className="btn-primary flex items-center gap-2 flex-shrink-0"
                style={{ opacity: aiSaving ? 0.7 : 1 }}>
                {aiSaving ? <RefreshCw size={13} className="animate-spin"/> : <Save size={13}/>}
                {aiSaved ? '✓ Saved!' : aiSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>

          {/* Feature cards */}
          <div className="space-y-4">
            {AI_FEATURE_DEFS.map(f => {
              const on = aiFeatures[f.key];
              return (
                <div key={f.key} className="rounded-2xl overflow-hidden"
                  style={{ border:`1px solid ${on ? 'rgba(139,92,246,0.25)' : 'rgba(255,255,255,0.06)'}`, background: on ? 'rgba(139,92,246,0.04)' : 'rgba(255,255,255,0.02)' }}>

                  {/* Feature header row — toggle + name + status */}
                  <div className="flex items-center gap-4 px-5 py-4" style={{ borderBottom:`1px solid ${on ? 'rgba(139,92,246,0.15)' : 'rgba(255,255,255,0.05)'}` }}>
                    <span className="text-2xl flex-shrink-0">{f.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm" style={{ color: on ? '#e2e8f0' : '#475569' }}>{f.label}</span>
                        <span className="text-xs" style={{ color:'#334155' }}>—</span>
                        <span className="text-xs italic" style={{ color:'#334155' }}>{f.tagline}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-xs px-2.5 py-1 rounded-full font-medium"
                        style={{ background: on ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.12)',
                                 color: on ? '#34d399' : '#f87171',
                                 border:`1px solid ${on ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.25)'}` }}>
                        {on ? '● Enabled' : '○ Disabled'}
                      </span>
                      <button type="button" onClick={() => setAiFeatures(prev => ({ ...prev, [f.key]: !prev[f.key] }))}
                        style={{ color: on ? '#a78bfa' : '#334155', transition:'color 0.2s' }}>
                        {on ? <ToggleRight size={32}/> : <ToggleLeft size={32}/>}
                      </button>
                    </div>
                  </div>

                  {/* Detail body */}
                  <div className="px-5 py-4 grid grid-cols-2 gap-5 text-xs">

                    {/* Left — what it does + how to use */}
                    <div className="space-y-3">
                      {/* What it does */}
                      <div>
                        <div className="font-semibold mb-1.5 flex items-center gap-1.5" style={{ color:'#60a5fa' }}>
                          <span>ℹ</span> What it does
                        </div>
                        <p style={{ color:'#64748b', lineHeight:1.6 }}>{f.desc}</p>
                      </div>

                      {/* Steps */}
                      <div>
                        <div className="font-semibold mb-1.5 flex items-center gap-1.5" style={{ color:'#34d399' }}>
                          <span>▶</span> How to use it
                        </div>
                        <ol className="space-y-1" style={{ color:'#64748b' }}>
                          {f.steps.map((s, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold mt-0.5"
                                style={{ background:'rgba(99,102,241,0.2)', color:'#818cf8', fontSize:9 }}>{i+1}</span>
                              <span style={{ lineHeight:1.5 }}>{s}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    </div>

                    {/* Right — where to find it + who can use it */}
                    <div className="space-y-3">
                      {/* Where */}
                      <div className="rounded-xl p-3.5" style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)' }}>
                        <div className="font-semibold mb-2 flex items-center gap-1.5" style={{ color:'#f59e0b' }}>
                          <span>📍</span> Where to find it
                        </div>
                        <div className="mb-1" style={{ color:'#94a3b8' }}><strong style={{ color:'#e2e8f0' }}>Location:</strong> {f.where}</div>
                        <div className="mt-2 px-3 py-2 rounded-lg text-xs" style={{ background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.2)', color:'#d97706', lineHeight:1.6 }}>
                          🗺 {f.nav}
                        </div>
                      </div>

                      {/* Who can use */}
                      <div className="rounded-xl p-3.5" style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)' }}>
                        <div className="font-semibold mb-2 flex items-center gap-1.5" style={{ color:'#818cf8' }}>
                          <span>👥</span> Who can use it
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {f.roles.map(r => (
                            <span key={r} className="px-2 py-0.5 rounded-full text-xs"
                              style={{ background:'rgba(99,102,241,0.15)', color:'#a5b4fc', border:'1px solid rgba(99,102,241,0.25)' }}>
                              {r}
                            </span>
                          ))}
                        </div>
                        {!on && (
                          <div className="mt-2 text-xs" style={{ color:'#f87171' }}>
                            ⚠ Currently disabled — users cannot see or use this feature
                          </div>
                        )}
                      </div>
                    </div>

                  </div>
                </div>
              );
            })}
          </div>

          {/* Save bar */}
          <div className="flex items-center gap-4 p-4 rounded-xl" style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)' }}>
            <span className="text-xs flex-1" style={{ color:'#475569' }}>
              Changes are saved to the database and take effect immediately. The UI updates within 60 seconds for all active sessions.
            </span>
            <button onClick={saveAiFeatures} disabled={aiSaving}
              className="btn-primary flex items-center gap-2"
              style={{ opacity: aiSaving ? 0.7 : 1 }}>
              {aiSaving ? <RefreshCw size={13} className="animate-spin"/> : <Save size={13}/>}
              {aiSaved ? '✓ Changes Saved!' : aiSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>

        </div>
      )}

      {/* ── USER MODAL ── */}
      {modal === 'user' && (
        <Modal title={selected ? 'Edit User' : 'Add User'} onClose={() => { setModal(null); setSelected(null); }}>
          <form onSubmit={handleSaveUser} className="space-y-3">
            <ErrMsg msg={error}/>
            <div className="grid grid-cols-2 gap-3">
              <FF form={form} setForm={setForm} label="Full Name *" name="full_name" required/>
              <FF form={form} setForm={setForm} label="Username *" name="username" required/>
              <FF form={form} setForm={setForm} label="Email *" name="email" type="email" required/>
              {!selected && <FF form={form} setForm={setForm} label="Password *" name="password" type="password" required/>}
              <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Role</label>
                <select value={form.role||'it_staff'} onChange={e=>setForm({...form,role:e.target.value})} className="input-dark">
                  {ROLES.map(r=><option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                </select>
              </div>
              <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Active</label>
                <select value={form.is_active===false?'false':'true'} onChange={e=>setForm({...form,is_active:e.target.value==='true'})} className="input-dark">
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
            </div>

            {/* Multi-company access — shown for restricted roles */}
            {['director','finance_staff','general_user','junior_it'].includes(form.role) && (
              <div>
                <label className="block text-xs font-medium mb-2" style={{color:'#475569'}}>
                  Company Access — select which companies this user can view
                  <span className="ml-2 text-xs" style={{color:'#334155'}}>(none = no data access)</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {companies.map(c => (
                    <label key={c.id} className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors"
                      style={{background: userCompanyAccess.includes(c.id) ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.03)',
                              border: `1px solid ${userCompanyAccess.includes(c.id) ? 'rgba(59,130,246,0.35)' : 'rgba(255,255,255,0.06)'}`}}>
                      <input type="checkbox" checked={userCompanyAccess.includes(c.id)}
                        onChange={e => setUserCompanyAccess(prev =>
                          e.target.checked ? [...prev, c.id] : prev.filter(id => id !== c.id)
                        )} className="accent-blue-500"/>
                      <div>
                        <div className="text-xs font-medium" style={{color:'#e2e8f0'}}>{c.name}</div>
                        <div className="text-xs font-mono" style={{color:'#475569'}}>{c.code}</div>
                      </div>
                    </label>
                  ))}
                </div>
                {userCompanyAccess.length > 0 && (
                  <div className="text-xs mt-2" style={{color:'#60a5fa'}}>
                    ✓ Access to: {companies.filter(c => userCompanyAccess.includes(c.id)).map(c => c.name).join(', ')}
                  </div>
                )}
              </div>
            )}

            {['admin','it_staff','junior_it'].includes(form.role) && (
              <div className="px-3 py-2 rounded-xl text-xs" style={{background:'rgba(16,185,129,0.08)',color:'#34d399',border:'1px solid rgba(16,185,129,0.2)'}}>
                ✓ Admin, IT Staff and Junior IT see all companies automatically
              </div>
            )}

            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => { setModal(null); setSelected(null); setUserCompanyAccess([]); }} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">{saving?'Saving...':'Save'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── COMPANY MODAL ── */}
      {modal === 'company' && (
        <Modal title={selected ? 'Edit Company' : 'Add Company'} onClose={() => { setModal(null); setSelected(null); }}>
          <form onSubmit={handleSaveCompany} className="space-y-3">
            <ErrMsg msg={error}/>
            <div className="grid grid-cols-2 gap-3">
              <FF form={form} setForm={setForm} label="Company Code *" name="code" required placeholder="e.g. CLS, Log-365"/>
              <FF form={form} setForm={setForm} label="Company Name *" name="name" required placeholder="Full legal name"/>
            </div>
            <FF form={form} setForm={setForm} label="Address" name="address" rows={2}/>
            <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Status</label>
              <select value={form.is_active===false?'false':'true'} onChange={e=>setForm({...form,is_active:e.target.value==='true'})} className="input-dark">
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => { setModal(null); setSelected(null); }} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">{saving?'Saving...':'Save'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── DEPARTMENT MODAL ── */}
      {modal === 'dept' && (
        <Modal title={selected ? 'Edit Department' : 'Add Department'} onClose={() => { setModal(null); setSelected(null); }}>
          <form onSubmit={handleSaveDept} className="space-y-3">
            <ErrMsg msg={error}/>
            <FF form={form} setForm={setForm} label="Department Name *" name="name" required/>
            <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Company *</label>
              <select required value={form.company_id||''} onChange={e=>setForm({...form,company_id:e.target.value})} className="input-dark">
                <option value="">Select company</option>
                {companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Status</label>
              <select value={form.is_active===false?'false':'true'} onChange={e=>setForm({...form,is_active:e.target.value==='true'})} className="input-dark">
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => { setModal(null); setSelected(null); }} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">{saving?'Saving...':'Save'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* EMAIL NOTICES TAB */}
      {tab === 'notices' && isAdmin && (
        <div className="space-y-4 max-w-2xl">
          <div className="glass glow-border rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Bell size={16} style={{color:'#60a5fa'}}/>
              <span className="font-semibold text-sm" style={{color:'#e2e8f0'}}>Send Email Notice to Employees</span>
            </div>
            <div className="text-xs mb-3" style={{color:'#475569'}}>
              Select a target audience, compose your message, and send a bulk email notice to matching employees.
            </div>

            {/* Target Mode Toggle */}
            <div className="flex gap-2 mb-4">
              {[['company','By Company / Dept'],['users','By Specific Employees']].map(([m,l]) => (
                <button key={m} type="button" onClick={() => {
                  setNoticeMode(m); setNoticePreview([]); setNoticeSent(null); setNoticeError('');
                  if (m === 'users' && allNoticeUsers.length === 0) {
                    api.get('/employees', { params: { limit: 500, status: 'active' } })
                      .then(r => setAllNoticeUsers((r.data.data || []).filter(e => e.email))).catch(() => {});
                  }
                }}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                  style={{ background: noticeMode===m?'rgba(59,130,246,0.2)':'rgba(255,255,255,0.03)', color: noticeMode===m?'#60a5fa':'#475569', border: noticeMode===m?'1px solid rgba(59,130,246,0.3)':'1px solid rgba(255,255,255,0.06)' }}>
                  {l}
                </button>
              ))}
            </div>

            <div className="space-y-4">
              {noticeMode === 'company' ? (
                <>
                  {/* Company */}
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Company</label>
                    <select value={noticeCompany} onChange={e => {
                      setNoticeCompany(e.target.value); setNoticeDept(''); setNoticePreview([]); setNoticeSent(null);
                      if (e.target.value) {
                        api.get('/employees/meta/departments', { params: { company_id: e.target.value } })
                          .then(r => setNoticeDepts(r.data || [])).catch(() => {});
                      } else { setNoticeDepts([]); }
                    }} className="input-dark">
                      <option value="">All Companies</option>
                      {companies.map(c => <option key={c.id} value={c.id}>{c.name} ({c.code})</option>)}
                    </select>
                  </div>
                  {/* Department */}
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Department</label>
                    <select value={noticeDept} onChange={e => { setNoticeDept(e.target.value); setNoticePreview([]); setNoticeSent(null); }} className="input-dark" disabled={!noticeCompany}>
                      <option value="">All Departments{noticeCompany ? '' : ' (select company first)'}</option>
                      {noticeDepts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>
                  {/* Preview recipients */}
                  <div>
                    <button type="button" onClick={async () => {
                      setNoticePreviewLoading(true); setNoticePreview([]);
                      try {
                        const params = { limit: 200, status: 'active' };
                        if (noticeCompany) params.company_id = noticeCompany;
                        if (noticeDept) params.department_id = noticeDept;
                        const r = await api.get('/employees', { params });
                        setNoticePreview((r.data.data || []).filter(e => e.email));
                      } catch {} finally { setNoticePreviewLoading(false); }
                    }} className="btn-ghost text-xs px-3 py-1.5 flex items-center gap-1.5">
                      <Users size={11}/>{noticePreviewLoading ? 'Loading...' : 'Preview Recipients'}
                    </button>
                    {noticePreview.length > 0 && (
                      <div className="mt-2 p-3 rounded-xl text-xs" style={{background:'rgba(59,130,246,0.06)',border:'1px solid rgba(59,130,246,0.2)'}}>
                        <span style={{color:'#60a5fa',fontWeight:600}}>{noticePreview.length} recipients</span>
                        <span style={{color:'#475569'}}> · {noticePreview.slice(0,5).map(e=>e.full_name).join(', ')}{noticePreview.length>5?` +${noticePreview.length-5} more`:''}</span>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                /* By Specific Employees mode */
                <div>
                  <label className="block text-xs font-medium mb-2" style={{color:'#475569'}}>Search and select employees</label>
                  <input
                    type="text" value={noticeUserSearch} onChange={e => setNoticeUserSearch(e.target.value)}
                    placeholder="Search by name..."
                    className="input-dark mb-2"/>
                  <div className="rounded-xl overflow-hidden" style={{border:'1px solid rgba(255,255,255,0.08)',maxHeight:220,overflowY:'auto'}}>
                    {allNoticeUsers.length === 0
                      ? <div className="p-3 text-xs" style={{color:'#475569'}}>Loading employees...</div>
                      : allNoticeUsers.filter(e => !noticeUserSearch || e.full_name?.toLowerCase().includes(noticeUserSearch.toLowerCase()))
                          .map(e => {
                            const sel = noticeUsers.includes(e.id);
                            return (
                              <button key={e.id} type="button"
                                onClick={() => { setNoticeUsers(prev => sel ? prev.filter(x => x !== e.id) : [...prev, e.id]); setNoticeSent(null); }}
                                style={{ display:'flex', alignItems:'center', gap:10, width:'100%', padding:'8px 12px', background: sel?'rgba(99,102,241,0.15)':'transparent', border:'none', borderBottom:'1px solid rgba(255,255,255,0.04)', cursor:'pointer', textAlign:'left' }}>
                                <span style={{ width:14, height:14, border:`1px solid ${sel?'#818cf8':'#334155'}`, borderRadius:3, background: sel?'rgba(99,102,241,0.3)':'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                  {sel && <span style={{color:'#818cf8',fontSize:9,fontWeight:'bold'}}>✓</span>}
                                </span>
                                <span style={{flex:1}}>
                                  <span className="text-xs font-medium" style={{color:'#e2e8f0'}}>{e.full_name}</span>
                                  {e.email && <span className="text-xs ml-2" style={{color:'#475569'}}>{e.email}</span>}
                                </span>
                              </button>
                            );
                          })
                    }
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs" style={{color: noticeUsers.length ? '#818cf8' : '#475569'}}>{noticeUsers.length} employee{noticeUsers.length !== 1 ? 's' : ''} selected</span>
                    {noticeUsers.length > 0 && <button type="button" onClick={() => setNoticeUsers([])} className="text-xs" style={{color:'#ef4444',background:'none',border:'none',cursor:'pointer'}}>Clear all</button>}
                  </div>
                  {noticeUsers.length === 0 && <div className="mt-1 text-xs" style={{color:'#f59e0b'}}>Select at least one employee.</div>}
                </div>
              )}

              {/* Subject */}
              <div>
                <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Subject *</label>
                <input type="text" value={noticeSubject} onChange={e => { setNoticeSubject(e.target.value); setNoticeSent(null); }}
                  placeholder="e.g. IT System Maintenance Notice" className="input-dark"/>
              </div>

              {/* Message */}
              <div>
                <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Message *</label>
                <textarea value={noticeMsg} onChange={e => { setNoticeMsg(e.target.value); setNoticeSent(null); }}
                  rows={6} className="input-dark" placeholder="Type your notice message here. It will be sent with the standard SynergyNode email template."/>
                <div className="text-xs mt-1" style={{color:'#334155'}}>Plain text. Line breaks are preserved in the email.</div>
              </div>

              {/* Attachment */}
              <div>
                <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Attachment <span style={{color:'#334155'}}>(optional — PDF, DOC, DOCX, max 25MB)</span></label>
                <div className="flex items-center gap-2">
                  <label className="btn-ghost text-xs cursor-pointer flex items-center gap-1.5 px-3 py-1.5">
                    <span>📎</span> {noticeFile ? noticeFile.name : 'Choose File'}
                    <input type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip"
                      onChange={e => { setNoticeFile(e.target.files[0] || null); setNoticeSent(null); }}/>
                  </label>
                  {noticeFile && (
                    <button type="button" onClick={() => setNoticeFile(null)} className="text-xs" style={{color:'#ef4444'}}>✕ Remove</button>
                  )}
                </div>
              </div>

              {/* Error / Success */}
              {noticeError && <div className="p-3 rounded-xl text-xs" style={{background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.25)',color:'#f87171'}}>{noticeError}</div>}
              {noticeSent && (
                <div className="p-3 rounded-xl text-xs" style={{background:'rgba(16,185,129,0.08)',border:'1px solid rgba(16,185,129,0.25)',color:'#34d399'}}>
                  ✓ Notice sent to <strong>{noticeSent.sent}</strong> recipients.
                  {noticeSent.skipped > 0 && <span style={{color:'#475569'}}> ({noticeSent.skipped} skipped — no email on file)</span>}
                </div>
              )}

              {/* Send button */}
              <button
                type="button"
                disabled={noticeSending || !noticeSubject.trim() || !noticeMsg.trim() || (noticeMode === 'users' && noticeUsers.length === 0)}
                onClick={async () => {
                  setNoticeSending(true); setNoticeError(''); setNoticeSent(null);
                  try {
                    const fd = new FormData();
                    if (noticeMode === 'users') {
                      fd.append('employee_ids', noticeUsers.join(','));
                    } else {
                      fd.append('company_id', noticeCompany || '');
                      fd.append('department_id', noticeDept || '');
                    }
                    fd.append('subject', noticeSubject.trim());
                    fd.append('message', noticeMsg.trim());
                    if (noticeFile) fd.append('attachment', noticeFile);
                    const r = await api.post('/settings/send-notice', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                    setNoticeSent(r.data);
                    setNoticeSubject(''); setNoticeMsg(''); setNoticePreview([]); setNoticeFile(null); setNoticeUsers([]);
                  } catch (err) {
                    setNoticeError(err.response?.data?.error || 'Failed to send notice');
                  } finally { setNoticeSending(false); }
                }}
                className="btn-primary w-full flex items-center justify-center gap-2"
                style={{opacity: (noticeSending || !noticeSubject.trim() || !noticeMsg.trim() || (noticeMode === 'users' && noticeUsers.length === 0)) ? 0.6 : 1}}>
                <Send size={13}/>{noticeSending ? 'Sending...' : 'Send Notice'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
